import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    
    // Если форма имеет data-product-form, слушаем cart:updated для обновления UI
    const form = this.querySelector('form');
    if (form && form.hasAttribute('data-product-form')) {
      document.addEventListener('cart:updated', this.#onCartUpdated, { signal });
    }
  }
  
  /**
   * Обработчик события cart:updated для форм с data-product-form
   * @param {CustomEvent} event
   */
  #onCartUpdated = (event) => {
    const cart = event.detail?.cart || event.detail;
    if (cart && !cart.description) { // description есть только при ошибке
      // Пропускаем инициализацию корзины
      if (event.detail?.isInitialLoad) {
        return;
      }
      
      // Показываем уведомление ТОЛЬКО если товар был только что добавлен (wasJustAdded)
      // Это предотвращает показ уведомления при загрузке страницы или других обновлениях корзины
      if (!event.detail?.wasJustAdded) {
        return;
      }
      
      // Товар успешно добавлен - обновляем UI
      this.#handleSuccessfulAdd(cart);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const form = this.querySelector('form');
    
    // Если форма имеет data-product-form, cart-form-handler.js обработает её
    // Мы полностью пропускаем обработку здесь, чтобы избежать конфликтов и рекурсии
    if (form && form.hasAttribute('data-product-form')) {
      // НЕ вызываем preventDefault() - позволяем событию всплыть до cart-form-handler.js
      // НЕ вызываем dispatchEvent() - это создаст бесконечную рекурсию
      // НЕ обрабатываем форму здесь - cart-form-handler.js сделает это
      // Просто выходим, чтобы не выполнить старую логику
      return;
    }
    
    const { addToCartTextError } = this.refs;
    
    // Старая логика для форм без data-product-form (обратная совместимость)
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          // Отправляем CartAddEvent (который является cart:update)
          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
          
          // Также отправляем cart:add и shopify:cart:update для обновления счетчиков
          // Получаем актуальные данные корзины
          fetch('/cart.js')
            .then(r => r.json())
            .then(cartData => {
              const itemCount = cartData.item_count || 0;
              const eventDetail = {
                cart: cartData,
                item_count: itemCount,
                data: { itemCount }
              };
              
              // Отправляем события для обновления счетчиков
              document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true, detail: eventDetail }));
              document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: eventDetail }));
              document.dispatchEvent(new CustomEvent('shopify:cart:update', { bubbles: true, detail: eventDetail }));
              
              // Показываем уведомление о добавлении товара
              this.#showAddNotification(this.dataset.productId);
            })
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Обрабатывает успешное добавление товара (когда форма обработана cart-form-handler.js)
   */
  #handleSuccessfulAdd(cart) {
    const form = this.querySelector('form');
    if (!form) return;
    
    const formData = new FormData(form);
    const id = formData.get('id');
    
    if (!id) return;
    
    // Обновляем UI (анимации, уведомления)
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
      const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
      const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
      
      this.#setLiveRegionText(addedText);
      setTimeout(() => {
        this.#clearLiveRegionText();
      }, 5000);
    }
    
    // Показываем уведомление
    this.#showAddNotification(this.dataset.productId);
    
    // Отправляем CartAddEvent для других компонентов
    this.dispatchEvent(
      new CartAddEvent({}, id.toString(), {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Показывает уведомление о добавлении товара в корзину
   * Берет данные (название и фото) прямо с текущей страницы продукта
   * @param {string} productId - ID товара (не используется, оставлен для совместимости)
   */
  #showAddNotification(productId) {
    // Вызываем глобальную функцию для показа уведомления
    if (window.showProductAddNotification) {
      // Передаем null, чтобы функция сама нашла данные на странице
      window.showProductAddNotification(null, this);
      return;
    }
    
    // Fallback: если глобальной функции нет, показываем локально
    this.#showAddNotificationLocal(productId);
  }
  
  /**
   * Локальная функция показа уведомления (fallback)
   * @param {string} productId - ID товара
   */
  #showAddNotificationLocal(productId) {
    const existing = document.querySelector('.product-add-notification');
    if (existing) existing.remove();

    // Берем данные прямо с текущей страницы продукта, где находится форма
    // Ищем заголовок товара на странице продукта
    let title = '';
    const titleEl = document.querySelector('[data-block-type="product-title"] h1, [data-block-type="product-title"] h2, [data-block-type="product-title"] h3, .product__title h1, .product__title h2, .product__title h3, h1.product-title, h1[data-product-title], .view-product-title h2');
    if (titleEl && titleEl.textContent.trim()) {
      title = titleEl.textContent.trim();
    } else {
      // Fallback: ищем любой заголовок на странице продукта
      const fallbackTitle = this.closest('.shopify-section-product-template, .product-page, .product-main')?.querySelector('h1, h2');
      if (fallbackTitle && fallbackTitle.textContent.trim()) {
        title = fallbackTitle.textContent.trim();
      } else {
        // Еще один fallback: ищем в карточке товара
        const cardTitle = document.querySelector('.product-card__title, .product-card [data-block-type="product-title"] p, .product-card [data-block-type="product-title"] a');
        if (cardTitle && cardTitle.textContent.trim()) {
          title = cardTitle.textContent.trim();
        }
      }
    }
    
    if (!title) {
      return;
    }

    // Ищем изображение товара на странице продукта
    let image = '';
    const imgEl = document.querySelector('.product__media img, .product-media img, .product-gallery img, .product__image img, [data-block-type="product-image"] img, .product-card__image img');
    if (imgEl && imgEl instanceof HTMLImageElement && imgEl.src) {
      image = imgEl.src;
    } else {
      // Fallback: ищем любое изображение продукта
      const fallbackImg = this.closest('.shopify-section-product-template, .product-page, .product-main')?.querySelector('img[src*="products"], img[src*="cdn.shopify"]');
      if (fallbackImg && fallbackImg instanceof HTMLImageElement && fallbackImg.src) {
        image = fallbackImg.src;
      }
    }

    const notification = document.createElement('div');
    notification.className = 'product-add-notification';
    notification.innerHTML = `
      <img src="${image}" alt="${title}" class="product-add-notification__image" />
      <div class="product-add-notification__content">
        <h4 class="product-add-notification__title" style="font-size:14px;margin:0 0 4px 0;font-weight:500;color:#3F4443;text-transform:uppercase;line-height:1.2;">${title}</h4>
        <p class="product-add-notification__message" style="font-size:13px;color:#3F4443;margin:0;line-height:1.2;text-transform:uppercase;">Successfully added to cart</p>
      </div>
    `;

    document.body.appendChild(notification);

    requestAnimationFrame(() => requestAnimationFrame(() => notification.classList.add('show')));

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

// Глобальная функция для показа уведомления о добавлении товара
// Может быть вызвана из любого места (cart-form-handler.js, quick-add-button.js и т.д.)
window.showProductAddNotification = function(item, contextElement) {
  const existing = document.querySelector('.product-add-notification');
  if (existing) existing.remove();

  let title = '';
  let image = '';
  
  // Если передан item из корзины, используем его данные
  if (item && item.product_title) {
    title = item.product_title;
    // Пытаемся найти изображение по product_id или variant_id
    if (item.image) {
      image = item.image;
    } else if (item.variant_id) {
      // Ищем карточку товара по variant_id
      const card = document.querySelector(`[data-variant-id="${item.variant_id}"], [data-product-id="${item.product_id}"]`);
      if (card) {
        const imgEl = card.querySelector('.product-card__image img, img[src*="products"], img[src*="cdn.shopify"]');
        if (imgEl && imgEl instanceof HTMLImageElement && imgEl.src) {
          image = imgEl.src;
        }
      }
    }
  }
  
  // Если не нашли данные из item, ищем на странице
  if (!title || !image) {
    // Ищем заголовок товара
    if (!title) {
      const titleEl = document.querySelector('[data-block-type="product-title"] h1, [data-block-type="product-title"] h2, [data-block-type="product-title"] h3, .product__title h1, .product__title h2, .product__title h3, h1.product-title, h1[data-product-title], .view-product-title h2, .product-card__title');
      if (titleEl && titleEl.textContent.trim()) {
        title = titleEl.textContent.trim();
      } else if (contextElement) {
        const fallbackTitle = contextElement.closest('.shopify-section-product-template, .product-page, .product-main')?.querySelector('h1, h2');
        if (fallbackTitle && fallbackTitle.textContent.trim()) {
          title = fallbackTitle.textContent.trim();
        }
      }
    }
    
    // Ищем изображение товара
    if (!image) {
      const imgEl = document.querySelector('.product__media img, .product-media img, .product-gallery img, .product__image img, [data-block-type="product-image"] img, .product-card__image img');
      if (imgEl && imgEl instanceof HTMLImageElement && imgEl.src) {
        image = imgEl.src;
      } else if (contextElement) {
        const fallbackImg = contextElement.closest('.shopify-section-product-template, .product-page, .product-main')?.querySelector('img[src*="products"], img[src*="cdn.shopify"]');
        if (fallbackImg && fallbackImg instanceof HTMLImageElement && fallbackImg.src) {
          image = fallbackImg.src;
        }
      }
    }
  }
  
  if (!title) {
    return;
  }

  const notification = document.createElement('div');
  notification.className = 'product-add-notification';
  notification.innerHTML = `
    <img src="${image}" alt="${title}" class="product-add-notification__image" />
    <div class="product-add-notification__content">
      <h4 class="product-add-notification__title" style="font-size:14px;margin:0 0 4px 0;font-weight:500;color:#3F4443;text-transform:uppercase;line-height:1.2;">${title}</h4>
      <p class="product-add-notification__message" style="font-size:13px;color:#3F4443;margin:0;line-height:1.2;text-transform:uppercase;">Successfully added to cart</p>
    </div>
  `;

  document.body.appendChild(notification);

  requestAnimationFrame(() => requestAnimationFrame(() => notification.classList.add('show')));

  setTimeout(() => {
    notification.classList.remove('show');
    setTimeout(() => notification.remove(), 300);
  }, 3000);
};

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
